import { useEffect, useState } from "react";
import { Link } from "react-router";
import { IconArrowRight, IconMenu2 } from "@tabler/icons-react";
import { Logo } from "./logo";
import { MobileMenu } from "./mobile-menu";

const NAV = [
  { href: "/", label: "Home" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/#services", label: "Services" },
];

export function Header() {
  const scrolled = useScrolled(16);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-[height] duration-300 ease-out ${
          scrolled ? "h-14" : "h-20"
        }`}
      >
        {/* Background layer — fades in on scroll. Header itself stays transparent. */}
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-0 border-b border-white/8 bg-topbar backdrop-blur-xl transition-opacity duration-200 ${
            scrolled ? "opacity-100" : "opacity-0"
          }`}
        />

        <div className="relative mx-auto flex h-full max-w-[1440px] items-center justify-between px-6 sm:px-8">
          <Link
            to="/"
            className="flex items-center gap-2 rounded-lg outline-hidden focus-visible:ring-2 focus-visible:ring-main-500"
          >
            <Logo />
          </Link>

          <nav className="hidden items-center gap-0.5 md:flex">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="rounded-md px-3.5 py-2 text-[15px] font-medium text-text-soft transition hover:bg-white/4 hover:text-text"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="https://app.titanskip.com"
              className="hidden h-10 items-center text-[15px] font-medium text-text-soft transition hover:text-text md:inline-flex"
            >
              Sign in
            </a>
            <a
              href="https://app.titanskip.com"
              className="group hidden h-10 items-center gap-1.5 rounded-full bg-main-500 pl-4 pr-1.5 text-[15px] font-semibold text-white transition hover:bg-main-400 md:inline-flex"
            >
              Get started
              <span
                aria-hidden
                className="grid h-7 w-7 place-items-center rounded-full bg-white/15 transition group-hover:translate-x-0.5 group-hover:bg-white/25"
              >
                <IconArrowRight size={12} stroke={2.6} />
              </span>
            </a>

            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              className="grid h-10 w-10 place-items-center rounded-lg border border-white/8 bg-white/5 text-text transition hover:border-white/15 hover:bg-white/8 md:hidden"
            >
              <IconMenu2 size={18} stroke={2} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        items={NAV}
      />
    </>
  );
}

/**
 * Tracks whether the page has been scrolled past `threshold` pixels.
 * SSR-safe (defaults to `false`); on client mount, initializes synchronously
 * from the real `window.scrollY` so a refresh-while-scrolled doesn't flash
 * the at-top state.
 */
function useScrolled(threshold) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const check = () => setScrolled(window.scrollY > threshold);
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, [threshold]);

  return scrolled;
}
